import { GridList, GridListItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { ArticleNameCell, BaseMoneyCell } from '@/components/ArticlesTableCells';
import {
  ArticleOrganizationPriceCardItem,
  ArticleWorkshopPriceCardItem,
} from '@/components/ArticlesTableCells/ArticleMoneyCell/ArticleMoneyCell';
import {
  ArticleServiceTypeCardItem,
  ArticleServiceTypeCell,
} from '@/components/ArticlesTableCells/ArticleServiceTypeCell';
import { ArticleStepper } from '@/components/ArticleStepper/ArticleStepper';
import { Card, CardContent, CardItems } from '@/components/Card/Card';
import { useNavigate } from '@/components/Link/Link';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import IconChevron from '@/icons/Chevron.svg';
import { hasActions, useArticleName } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { createBEMClasses } from '@/utils/classname';
import { shouldShowPendingOrganizationQuote, shouldShowPendingWorkshopQuote } from '@/utils/price';

import './ArchivedArticlesTable.css';

const { block, element } = createBEMClasses('archived-articles-table');

export const ArchivedArticlesTable = ({ articles }: { articles: ArticleWithRelations[] }) => {
  const { t } = useLingui();
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  return (
    <Table
      aria-label={t({ id: 'request.articles.table.label', message: 'Items' })}
      columnWidths={[
        'minmax(200px, 1fr)',
        'minmax(120px, 1fr)',
        'minmax(120px, 1fr)',
        workshopPrice.shown && 'minmax(120px, 1fr)',
        organizationPrice.shown && 'minmax(auto, 150px)',
      ]}
      className={block()}
    >
      <ArchivedArticlesTableHeader />
      <Body>
        {articles.map((article) => (
          <ArchivedArticlesTableRow key={article.id} article={article} />
        ))}
      </Body>
    </Table>
  );
};

const ArchivedArticlesTableHeader = () => {
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  return (
    <Header>
      <Row>
        <Column>
          <Trans id="request.articles.table.label">Items</Trans>
        </Column>
        <Column>
          <Trans id="request.articles.table.column.step.label">Step</Trans>
        </Column>
        <Column>
          <Trans id="request.articles.table.column.service-type.label">Service Type</Trans>
        </Column>
        {workshopPrice.shown && (
          <Column justifyContent="flex-end" textAlign="right">
            {workshopPrice.label}
          </Column>
        )}
        {organizationPrice.shown && (
          <Column justifyContent="flex-end" textAlign="right">
            {organizationPrice.label}
          </Column>
        )}
      </Row>
    </Header>
  );
};

const ArchivedArticlesTableRow = ({ article }: { article: ArticleWithRelations }) => {
  const { t } = useLingui();
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  const clientRefusalBadgeText =
    article.cancellationDetail?.type === 'requestor_refusal'
      ? t({
          id: 'request.articles.cancelled-by-client',
          message: 'Client refusal',
        })
      : undefined;

  return (
    <Row style={{ opacity: 0.3 }} to={`/requests/${article.requestId}/articles/${article.id}`}>
      <Cell isLink>
        <ArticleNameCell article={article} />
      </Cell>
      <Cell stretch>
        <ArticleStepper size="medium" article={article} mode="step" withoutStepper />
      </Cell>
      <Cell>
        <ArticleServiceTypeCell article={article} />
      </Cell>
      {workshopPrice.shown && (
        <Cell justifyContent="flex-end">
          <BaseMoneyCell
            price={article.snapshot.workshopPrice?.amountPerCurrency?.[0]}
            showAmountBeforeTaxes={workshopPrice.showAmountBeforeTaxes}
            emptyLabel={shouldShowPendingWorkshopQuote(article) ? undefined : '-'}
          />
        </Cell>
      )}
      {organizationPrice.shown && (
        <Cell justifyContent="flex-end">
          <BaseMoneyCell
            price={article.snapshot.organizationPrice?.amountPerCurrency?.[0]}
            clientRefusalBadgeText={clientRefusalBadgeText}
            showAmountBeforeTaxes={organizationPrice.showAmountBeforeTaxes}
            emptyLabel={shouldShowPendingOrganizationQuote(article) ? undefined : '-'}
          />
        </Cell>
      )}
    </Row>
  );
};

export const ArchivedArticlesCardList = ({ articles }: { articles: ArticleWithRelations[] }) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { request } = useRequestContext();

  return (
    <GridList
      className={block()}
      onAction={(key) => {
        navigate(`/requests/${request.id}/articles/${key}`);
      }}
      aria-label={t({
        id: 'request.articles.table.archived-articles.label',
        message: 'Archived items',
      })}
    >
      {articles.map((article) => (
        <ArchivedArticlesCard key={article.id} article={article} />
      ))}
    </GridList>
  );
};

const ArchivedArticlesCard = ({ article }: { article: ArticleWithRelations }) => {
  const { t } = useLingui();
  const {
    view: { workshopPrice, organizationPrice },
  } = useRequestContext();

  const clientRefusalBadgeText =
    article.cancellationDetail?.type === 'requestor_refusal'
      ? t({
          id: 'request.articles.cancelled-by-client',
          message: 'Client refusal',
        })
      : undefined;

  const articleName = useArticleName({ article });

  return (
    <GridListItem
      id={article.id}
      textValue={articleName}
      style={{ opacity: 0.3 }}
      className={element('item')}
    >
      <Card>
        <CardContent>
          <Stack
            row
            gap="0.5rem"
            flexWrap="nowrap"
            justifyContent="space-between"
            alignItems="center"
          >
            <ArticleNameCell id={article.id} article={article} />
            <IconChevron right style={{ fontSize: '1.5rem' }} />
          </Stack>
          <ArticleStepper size="medium" article={article} mode="step" withoutStepper />
          <CardItems>
            {hasActions(article) && <ArticleServiceTypeCardItem article={article} />}
            {workshopPrice.shown && <ArticleWorkshopPriceCardItem article={article} />}
            {organizationPrice.shown && (
              <ArticleOrganizationPriceCardItem
                article={article}
                clientRefusalBadgeText={clientRefusalBadgeText}
              />
            )}
          </CardItems>
        </CardContent>
      </Card>
    </GridListItem>
  );
};
